import React from 'react';
import { Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import CustomInput from 'src/components/common/customInput';
import LoadingComponent from 'src/components/common/LoadingComponent';
import { AiSparkleIcon } from './icons/AiSparkleIcon';
import { createUseStyles } from 'react-jss';

const useAiModalStyles = createUseStyles({
    aiInputOverlayContainer: {
        position: 'relative',
    },
    aiInputOverlay: {
        position: 'absolute',
        top: '-30px',
        right: '-5px',
        backgroundColor: 'white',
        padding: '0',
        boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
        zIndex: 1000,
        width: '500px',
        maxWidth: '90vw',
        borderRadius: '12px 0 0 12px',
        overflow: 'hidden',
    },
    aiModalHeader: {
        padding: '16px 24px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    aiModalTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '16px',
        fontWeight: 500,
        color: '#1f1f1f',
        '& svg': {
            color: '#1677ff',
        },
    },
    aiModalContent: {
        padding: '24px',
    },
    aiModalLabel: {
        fontSize: '14px',
        color: '#1f1f1f',
        marginBottom: '12px',
    },
    aiResponseSection: {
        marginTop: '24px',
    },
    aiModalActions: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginTop: '24px',
    },
    acceptButton: {
        width: '100%',
        height: '40px',
        background: '#1677ff !important',
        color: 'white !important',
        border: 'none !important',
        borderRadius: '6px !important',
        '&:hover': {
            background: '#4096ff !important',
        },
    },
    rejectButton: {
        width: '100%',
        height: '40px',
        background: 'white !important',
        color: '#1f1f1f !important',
        border: '1px solid #d9d9d9 !important',
        borderRadius: '6px !important',
        '&:hover': {
            background: '#fafafa !important',
        },
    },
    aiTextArea: {
        '& textarea': {
            resize: 'vertical !important',
            minHeight: '60px !important',
            maxHeight: '200px !important',
            width: '100% !important',
            overflowY: 'auto !important',
            whiteSpace: 'pre-wrap !important',
            wordWrap: 'break-word !important',
            border: '1px solid rgba(24,144,255,0.3)',
            borderRadius: '6px',
            transition: 'all 0.3s',
            '&:focus': {
                boxShadow: '0 0 0 2px rgba(24,144,255,0.2)',
                borderColor: '#1890ff',
            },
        },
    },
    loader: {
        fontSize: '40px',
        position: 'absolute',
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        top: 0,
        left: 0,
        zIndex: 10,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(4px)',
        borderRadius: '8px',
    },
    '@keyframes slideIn': {
        from: { 
            transform: 'translateY(-45%)',
            opacity: 0,
            right: '-600px',
        },
        to: { 
            transform: 'translateY(-50%)',
            opacity: 1,
            right: '0',
        },
    },
}); 

interface AiResponse {
    column_name: string;
    format: string;
    sql: string;
}

interface AiSqlModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAccept: (response: AiResponse) => void;
    aiPrompt: string;
    setAiPrompt: (prompt: string) => void;
    aiResponse: AiResponse | null;
    isLoading: boolean;
    onAskAi: () => void;
    onReject: () => void;
    showColumnName: boolean;
}

export const AiSqlModal: React.FC<AiSqlModalProps> = ({
    isOpen,
    onClose,
    onAccept,
    aiPrompt,
    setAiPrompt,
    aiResponse,
    isLoading,
    onAskAi,
    onReject,
    showColumnName,
}) => {
    const classes = useAiModalStyles();
    const { t } = useTranslation();

    if (!isOpen) return null;

    return (
        <div className={classes.aiInputOverlayContainer}>
        <div className={classes.aiInputOverlay}>
            <div className={classes.aiModalHeader}>
                <div className={classes.aiModalTitle}>
                    <AiSparkleIcon />
                    Ask AI to generate SQL
                </div>
                <CloseOutlined 
                    onClick={onClose} 
                    style={{ cursor: 'pointer', fontSize: '16px', color: 'rgba(0,0,0,0.45)' }} 
                />
            </div>
            
            <div className={classes.aiModalContent}>
                {isLoading && (
                    <div className={classes.loader}>
                        <LoadingComponent style={{ backgroundColor: 'transparent' }}/>
                    </div>
                )}
                
                <div className={classes.aiModalLabel}>
                    Describe what you want in plain language
                </div>
                
                <CustomInput
                    onChange={setAiPrompt}
                    value={aiPrompt}
                    placeholder={t('i want columns of consignment')}
                    textarea={true}
                    className={classes.aiTextArea}
                    disabled={!!aiResponse}
                />
                
                {!aiResponse ? (
                    <div className={classes.aiModalActions}>
                        <Button 
                            className={classes.acceptButton}
                            onClick={onAskAi}
                            disabled={isLoading || !aiPrompt.trim()}
                        >
                            {t('Generate')}
                        </Button>
                    </div>
                ) : (
                    <div className={classes.aiResponseSection}>
                        <div className={classes.aiModalLabel}>Column Details</div>
                        <div style={{ 
                            background: '#f5f5f5', 
                            padding: '12px', 
                            borderRadius: '6px', 
                            marginBottom: '16px',
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '12px',
                        }}>
                            {showColumnName ? <div>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Column Name</div>
                                <div style={{ fontWeight: 500 }}>{aiResponse.column_name}</div>
                            </div> : null}
                            <div>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Column Type</div>
                                <div style={{ fontWeight: 500 }}>{aiResponse.format}</div>
                            </div>
                        </div>

                        <div className={classes.aiModalLabel}>Generated SQL</div>
                        <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '6px', marginBottom: '24px' }}>
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{aiResponse.sql}</pre>
                        </div>
                        <div className={classes.aiModalActions}>
                            <Button className={classes.acceptButton} onClick={() => onAccept(aiResponse)}>
                                {t('Accept')}
                            </Button>
                            <Button className={classes.rejectButton} onClick={onReject}>
                                {t('Reject')}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
        </div>
    );
}; 